import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface PairingState {
  bracketPositions: Array<{ poet: string; poemIndex: number; poemId?: string }>;
  pairings: Array<[number, number]>;
  subject?: string;
}

// Local storage keys for progress
const STORAGE_KEYS = {
  CURRENT_PAIRING_INDEX: 'deathmatch_current_pairing_index',
  WINNERS: 'deathmatch_winners',
  VIEW_SUMMARY: 'deathmatch_view_summary',
};

export const useContestState = (initialState: PairingState) => {
  const navigate = useNavigate();
  const { bracketPositions: initialBracketPositions, pairings: initialPairings } = initialState;

  // Initialize state directly, potentially loading progress from localStorage
  const loadInitialProgress = () => {
     // Only attempt load if initialPairings is valid (parent should ensure this)
     if (initialPairings.length === 0) {
         return {
             currentPairingIndex: 0,
             winners: [],
             isViewingSummary: false,
         };
     }
     try {
       const storedPairingIndex = parseInt(localStorage.getItem(STORAGE_KEYS.CURRENT_PAIRING_INDEX) || '0', 10);
       const storedWinners = JSON.parse(localStorage.getItem(STORAGE_KEYS.WINNERS) || 'null');
       const storedViewSummary = localStorage.getItem(STORAGE_KEYS.VIEW_SUMMARY) === 'true';

       // Validate stored winners against current pairings length
       if (storedWinners && Array.isArray(storedWinners) && storedWinners.length === initialPairings.length) {
         console.log("[useContestState] Loaded progress state from localStorage.");
         return {
           currentPairingIndex: storedPairingIndex,
           winners: storedWinners,
           isViewingSummary: storedViewSummary,
         };
       } else {
            console.warn("[useContestState] localStorage winners length mismatch or invalid. Starting fresh progress.");
       }
     } catch (error) {
       console.error("[useContestState] Error loading progress from localStorage:", error);
     }
     // Default: If load fails or invalid
     console.log("[useContestState] Setting default initial progress state.");
     return {
       currentPairingIndex: 0,
       winners: Array(initialPairings.length).fill(null),
       isViewingSummary: false,
     };
  };

  // Use a ref to ensure loadInitialProgress runs only once effectively
  const initialProgressRef = useRef(loadInitialProgress());

  const [currentPairingIndex, setCurrentPairingIndex] = useState(initialProgressRef.current.currentPairingIndex);
  const [winners, setWinners] = useState<Array<number | null>>(initialProgressRef.current.winners);
  const [isViewingSummary, setIsViewingSummary] = useState(initialProgressRef.current.isViewingSummary);

  // Recalculate initial winners ONLY if initialPairings length changes drastically (e.g., from 0 to 7)
  // This helps sync state if the hook initializes before parent has data.
  useEffect(() => {
    if (winners.length !== initialPairings.length && initialPairings.length > 0) {
        console.warn("[useContestState] Syncing winners array length due to initialPairings update.");
        setWinners(Array(initialPairings.length).fill(null));
        // Optionally reset index and summary view if length mismatch implies a completely new contest setup
        // setCurrentPairingIndex(0);
        // setIsViewingSummary(false);
    }
    // Run only when initialPairings changes, specifically its length
  }, [initialPairings.length]);

  // Calculate whether the contest is completed
  const isContestCompleted = useMemo(() => {
    return winners.every(winner => winner !== null);
  }, [winners]);

  // Calculate the champion position from the winners array
  const championPosition = useMemo(() => {
    if (!isContestCompleted || !winners[winners.length - 1]) return null;
    return winners[winners.length - 1];
  }, [winners, isContestCompleted]);

  // Update local storage when PROGRESS state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CURRENT_PAIRING_INDEX, currentPairingIndex.toString());
      localStorage.setItem(STORAGE_KEYS.WINNERS, JSON.stringify(winners));
      localStorage.setItem(STORAGE_KEYS.VIEW_SUMMARY, isViewingSummary.toString());
      // DO NOT save bracketPositions or pairings here - they come from component props
    } catch (error) {
      console.error("Error saving state to local storage:", error);
    }
  }, [currentPairingIndex, winners, isViewingSummary]); // Dependencies updated

  const handlePoemWin = (position: number) => {
    setWinners(prev => {
      const newWinners = [...prev];
      newWinners[currentPairingIndex] = position;
      // Logic for updating future rounds (indices 4, 5, 6) based on completed rounds
      // This logic seems specific to an 8-person bracket and might need adjustment
      if (currentPairingIndex === 3 && newWinners.slice(0, 4).every(w => w !== null)) {
        newWinners[4] = null; 
        newWinners[5] = null; 
        console.log("First round completed, reset semifinals");
      }
      if (currentPairingIndex === 5 && newWinners[4] !== null && newWinners[5] !== null) {
        newWinners[6] = null; 
        console.log("Semifinals completed, reset final");
      }
      return newWinners;
    });
  };

  const navigateToPairing = (direction: 'next' | 'prev') => {
    if (isViewingSummary) {
      setIsViewingSummary(false);
      return;
    }
    if (direction === 'next' && currentPairingIndex < initialPairings.length - 1) {
      setCurrentPairingIndex(prev => prev + 1);
    } else if (direction === 'prev' && currentPairingIndex > 0) {
      setCurrentPairingIndex(prev => prev - 1);
    }
  };

  const toggleSummaryView = (show?: boolean) => {
    if (show !== undefined) {
      setIsViewingSummary(show);
    } else {
      setIsViewingSummary(prev => !prev);
    }
  };

  const currentPairing = initialPairings[currentPairingIndex];
  const winner = winners[currentPairingIndex];

  return {
    currentPairingIndex,
    currentPairing,
    winner,
    winners,
    isContestCompleted,
    championPosition,
    isViewingSummary,
    handlePoemWin,
    navigateToPairing,
    toggleSummaryView,
    totalPairings: initialPairings.length,
    setCurrentPairingIndex,
  };
};
